import React from 'react';
import Plot from 'react-plotly.js';
import { PoolDataHookResult } from '../../types/pool';
import { POOL_CONFIGS, getPoolColor, DEX_INFO } from '../../config/pools';
import { getPieChartLayout, getBaseConfig } from './chartConfig';
import './ChartStyles.css';

interface TVLPieChartProps {
  poolsData: { [key: string]: PoolDataHookResult };
  className?: string;
}

// Helper function to get shorter DEX abbreviations for legends
const getDEXAbbreviation = (dex: string): string => {
  switch (dex) {
    case 'uniswap_v3':
      return 'Uni V3';
    case 'pancakeswap_v3':
      return 'Pancake V3';
    case 'uniswap_v4':
      return 'Uni V4';
    case 'sushiswap_v3':
      return 'Sushi V3';
    default:
      return dex;
  }
};

export const TVLPieChart: React.FC<TVLPieChartProps> = ({ poolsData, className }) => {
  // Prepare data for the pie chart
  const chartData = React.useMemo(() => {
    const labels: string[] = [];
    const values: number[] = [];
    const colors: string[] = [];
    
    POOL_CONFIGS.forEach((poolConfig) => {
      const poolKey = `${poolConfig.dex}_${poolConfig.address}`;
      const poolData = poolsData[poolKey];
      
      if (poolData?.snapshots && poolData.snapshots.length > 0) {
        const latestSnapshot = poolData.snapshots[poolData.snapshots.length - 1];
        const tvl = latestSnapshot.total_value_locked_usd || 0;
        
        if (tvl > 0) {
          labels.push(`${getDEXAbbreviation(poolConfig.dex)} - ${poolConfig.name}`);
          values.push(tvl);
          colors.push(getPoolColor(poolConfig));
        }
      }
    });
    
    return {
      labels,
      values,
      colors
    };
  }, [poolsData]);

  const data = [{
    labels: chartData.labels,
    values: chartData.values,
    type: 'pie' as const,
    marker: {
      colors: chartData.colors,
      line: {
        color: '#404040',
        width: 2
      }
    },
    textinfo: 'percent' as const,
    textposition: 'inside' as const,
    textfont: {
      color: '#ffffff',
      size: 10
    },
    insidetextorientation: 'horizontal',
    hovertemplate: '<b>%{label}</b><br>' +
                   'TVL: $%{value:,.0f}<br>' +
                   'Percentage: %{percent}<br>' +
                   '<extra></extra>'
  }];

  const layout = {
    ...getPieChartLayout('Liquidity Pool Asset Allocation'),
    height: 450, // Increased for better proportions
    showlegend: false, // Remove legend, keep only hover
    margin: { t: 20, r: 50, b: 60, l: 60 } // Adjust margins since no legend space needed
  };

  return (
    <div className={`chart-container ${className || ''}`}>
      <h2 className="chart-title ev-accent separator-wide">Liquidity Pool Asset Allocation</h2>
      <Plot
        data={data}
        layout={layout}
        style={{ width: '100%', height: '450px' }}
        config={getBaseConfig()}
      />
    </div>
  );
};
